import { cn } from '@plato/ui/util/classes';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useResolvedLocale } from '../../lib/hooks/locale.ts';
import type { FormatValueStringFormat } from './format-value-string.ts';
import { formatValueString } from './format-value-string.ts';

export type RenderLinkProps = {
    email?: boolean;
    value: string;
};

export type FormatValueProps = {
    dateTimeFormatOptions?: Intl.DateTimeFormatOptions;
    fallback?: ReactNode;
    locale?: string;
    numberFormatOptions?: Intl.NumberFormatOptions;
    renderLink?: (props: RenderLinkProps) => ReactElement;
    stringFormat?: FormatValueStringFormat;
    value?: unknown;
};

/**
 * Formats an arbitrary value for display.
 */
export function FormatValue({
    fallback = null,
    locale: externalLocale,
    renderLink: Link = FormatLink,
    ...formatProps
}: FormatValueProps): ReactElement {
    const { t } = useTranslation();
    const appLocale = useResolvedLocale();
    const locale = externalLocale ?? appLocale;
    const { stringFormat, value } = formatProps;

    if (value === undefined || value === null || value === '') {
        return <>{fallback}</>;
    }

    // links are the only value that renders as markup rather than text
    if (typeof value === 'string' && (stringFormat === 'email' || stringFormat === 'uri')) {
        return <Link email={stringFormat === 'email'} value={value} />;
    }

    return <>{formatValueString({ ...formatProps, locale, t })}</>;
}

export type FormatLinkProps = RenderLinkProps & {
    className?: string;
};

export function FormatLink({ className, email, value }: FormatLinkProps): ReactElement {
    const trimmed = value.trim();

    let url: URL | undefined;
    try {
        if (email) {
            url = new URL(trimmed);
        } else if (/^[a-z0-9-]:/i.test(trimmed)) {
            url = new URL(trimmed);
        } else {
            url = new URL(`https://${trimmed}`);
        }
    } catch {
        // not a valid URL, we'll just render the text
    }

    if (!url) {
        return <>{trimmed}</>;
    }

    let linkText = url.href.slice(url.protocol.length);
    if (linkText.startsWith('//')) {
        linkText = linkText.slice(2);
    }
    return (
        <a
            className={cn('text-blue-500', className)}
            href={url.href}
            target="_blank"
            rel="noopener noreferrer"
        >
            {linkText}
        </a>
    );
}
